import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { Button } from 'src/shared/components/ui/buttons/button'
import FilterGrain from 'src/shared/components/ui/image/filter-grain'
import WebmVideo from 'src/shared/components/ui/video/webm-video'
import GolfVideoFallback from 'src/shared/features/enterprise/assets/golf-vid.mov'
import GolfVideoWebm from 'src/shared/features/enterprise/assets/golf-vid.webm'
import BaseballVideoFallback from 'src/shared/features/home/assets/baseball-vid.mov'
import BaseballVideoWebm from 'src/shared/features/home/assets/baseball-vid.webm'
// TODO: Add webm video
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { cn } from 'src/shared/utils/cn'

import useScheduleDemoStore from './stores/use-schedule-demo-store'

const ConsumerSideContent = () => {
  return (
    <div className="relative flex flex-1">
      <WebmVideo
        src={BaseballVideoWebm}
        fallbackSrc={BaseballVideoFallback}
        className="size-full object-cover"
        containerClassName="absolute inset-0"
        loop={true}
        autoPlay={true}
        muted={true}
      />
      <p className="absolute inset-x-0 bottom-0 px-[54px] pb-[54px] text-[36px] font-medium leading-[1.3] tracking-[-0.84px] text-white lg:pb-[78px] lg:text-[42px]">
        Where home happens.
      </p>
    </div>
  )
}

const EnterpriseSideContent = () => {
  const location = useLocation()
  const { isSchedulingDemo, setIsSchedulingDemo } = useScheduleDemoStore()

  const showScheduleDemoButton = useMemo(() => {
    // Only show schedule demo on login pages. Add to this list if needed.
    const visiblePaths: string[] = [paths.enterprise.login.path, paths.enterprise.loginPhone.path]
    return visiblePaths.some((path) => location.pathname.startsWith(path))
  }, [location.pathname])

  return (
    <div className="relative flex flex-1">
      <WebmVideo
        src={GolfVideoWebm}
        fallbackSrc={GolfVideoFallback}
        className="size-full object-cover"
        containerClassName="absolute inset-0"
        loop={true}
        autoPlay={true}
        muted={true}
      />
      <FilterGrain />
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-start justify-end gap-4 px-[50px] pb-[54px] lg:pb-[78px]">
        <p className="text-[36px] font-medium leading-[1.3] tracking-[-0.84px] text-white lg:text-[42px]">
          Spend time on relationships, not software.
        </p>
        {showScheduleDemoButton && (
          <Button
            className="rounded-full bg-white px-8 font-medium text-primary"
            onClick={() => setIsSchedulingDemo(!isSchedulingDemo)}
          >
            {isSchedulingDemo ? 'Sign-up' : 'Schedule demo'}
          </Button>
        )}
      </div>
    </div>
  )
}

interface EnterpriseAuthSideContentProps {
  variant?: 'default' | 'consumer'
}

export default function EnterpriseAuthSideContent({ variant = 'default' }: EnterpriseAuthSideContentProps) {
  const { isMobile } = useBreakpoint()
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      setIsVisible(true)
    })

    return () => {
      cancelAnimationFrame(frameId)
    }
  }, [])

  return (
    <div
      className={cn(
        'relative flex flex-1 flex-col overflow-hidden rounded-[40px] bg-white transition-opacity duration-300 ease-out',
        isVisible ? 'opacity-100' : 'opacity-0',
        isMobile && 'w-full border border-border shadow-[0_2px_20px_0_rgba(27,48,41,0.05)]',
      )}
    >
      {variant === 'default' && <EnterpriseSideContent />}
      {variant === 'consumer' && <ConsumerSideContent />}
    </div>
  )
}
